'use client'

import DatoLink from '@/components/DatoLink/DatoLink'
import { HeaderQuery } from '@/components/Header/Header.generated'
import { LocalizedLink } from '@/components/LocalizedLink'
import {
	InternalLinkRecord,
	ResponsiveImage,
	StoryRecord,
} from '@/graphql/types'
import getUrlPrefix, { urlsPrefix } from '@/utils/getUrlPrefix'
import { transformLocale } from '@/utils/language'
import {
	cn,
	getGridCols,
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from '@desoutter/uikit'
import { Button } from '@desoutter/uikit/src/components/atoms/Button'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { ReactNode, useMemo, useState } from 'react'
import { Image as DatoImage } from 'react-datocms'
import { DatoLinkFragment } from '@/components/DatoLink/DatoLink.generated'

export function HeaderMenu({
	menu,
	language,
}: {
	menu: HeaderQuery['header']['menu']
	language: string
}) {
	const pathname = usePathname()
	const [value, setValue] = useState<string | null>(null)

	// Transform language from DatoCMS format to URL format for pathname comparison
	const urlLanguage = transformLocale({
		locale: language,
		to: 'params',
	})

	const activeId = useMemo(() => {
		const pathnamePrefixes = new Set(
			menu.flatMap((menuItem) => {
				const prefixes = []
				if (menuItem.simpleLink?.id) {
					prefixes.push({
						id: menuItem.id,
						url: getUrlPrefix(menuItem.simpleLink as InternalLinkRecord),
					})
					/** 💩 Quick and dirty fix to keep the blog menu item underlined while user is in /articles */
					if (menuItem.label === 'Blog') {
						prefixes.push({
							id: menuItem.id,
							url: '/articles',
						})
					}
					/** 💩 End of quick fix */
				}

				if (menuItem.blocks?.length) {
					/** 💩 Quick and dirty fix to keep the Company menu item underlined while user is in /sustainability */
					if (menuItem.label === 'Company') {
						prefixes.push({
							id: menuItem.id,
							url: '/sustainability',
						})
					}
					/** 💩 End of quick fix */

					menuItem.blocks.forEach((block) => {
						if (block.titleLink.__typename === 'InternalLinkRecord') {
							prefixes.push({
								id: menuItem.id,
								url: urlsPrefix[block.titleLink.link.__typename],
							})
						}
						if (block.links) {
							block.links.forEach((link) => {
								if (link && link.__typename === 'InternalLinkRecord') {
									prefixes.push({
										id: menuItem.id,
										url: urlsPrefix[link.link.__typename],
									})
								}
							})
						}
					})
				}

				if (menuItem.stories?.length) {
					menuItem.stories.forEach((story) => {
						if (story.link && story.link.__typename === 'InternalLinkRecord') {
							prefixes.push({
								id: menuItem.id,
								url: urlsPrefix[story.link.__typename],
							})
						}
					})
				}

				return prefixes
			}),
		)

		let BreakException = {}
		let activeId: string | undefined
		try {
			pathnamePrefixes.forEach((prefix) => {
				if (pathname.startsWith(`/${urlLanguage}${prefix.url}`)) {
					activeId = prefix.id
					// throw BreakException
				}
			})
		} catch (e) {
			if (e !== BreakException) throw e
		}

		return activeId
	}, [pathname, urlLanguage])

	return (
		<NavigationMenu
			className="hidden lg:flex"
			onValueChange={setValue}
			value={value}
		>
			<NavigationMenuList className="space-x-0 xl:gap-4">
				{menu.map((menuItem, index) =>
					menuItem.simpleLink?.id ? (
						<NavigationMenuLink
							key={index}
							aria-current={activeId === menuItem.id}
							className="group inline-flex w-max items-center justify-center rounded-md px-2 xl:px-4 py-2 text-sm xl:text-base font-medium transition-colors hover:bg-background/50 disabled:pointer-events-none disabled:opacity-5 aria-[current=true]:nav-active"
							asChild
						>
							<LocalizedLink
								locale={urlLanguage}
								href={getUrlPrefix(menuItem.simpleLink as InternalLinkRecord)}
							>
								{menuItem.label}
							</LocalizedLink>
						</NavigationMenuLink>
					) : (
						<NavigationMenuItem key={index}>
							<NavigationMenuTrigger
								aria-current={activeId === menuItem.id}
								className={cn(
									'text-sm px-2 xl:px-4 xl:text-base aria-[current=true]:nav-active',
								)}
							>
								{menuItem.label}
							</NavigationMenuTrigger>
							<NavigationMenuContent>
								<DropdownMenuContentWrapper
									colsNumber={menuItem.blocks.length}
									extraButtons={menuItem.extraButtons as InternalLinkRecord[]}
									stories={menuItem.stories as StoryRecord[]}
									closeMenu={() => setValue(null)}
								>
									{menuItem.blocks.map((block) => (
										<DropdownMenuContentBlock
											title={block.title}
											description={block.description}
											image={block.image?.responsiveImage as ResponsiveImage}
											titleLink={block.titleLink as InternalLinkRecord}
											links={block.links as InternalLinkRecord[]}
											colsNumber={menuItem.blocks.length}
											showIcon={block.showIcons}
											showLinkDescriptions={block.showLinkDescriptions}
											key={block.id}
											closeMenu={() => setValue(null)}
										/>
									))}
								</DropdownMenuContentWrapper>
							</NavigationMenuContent>
						</NavigationMenuItem>
					),
				)}
			</NavigationMenuList>
		</NavigationMenu>
	)
}

export function DropdownMenuContentWrapper({
	children,
	colsNumber,
	extraButtons,
	stories,
	closeMenu,
}: {
	children: ReactNode
	colsNumber: number
	extraButtons?: InternalLinkRecord[]
	stories?: StoryRecord[]
	closeMenu?: () => void
}) {
	return (
		<div className="lg:min-w-[1000px] xl:min-w-[1280px] 2xl:min-w-[1400px] max-w-[98vw] p-6 z-10">
			<div className={cn('grid gap-6', getGridCols(colsNumber))}>
				{children}
			</div>
			<div className="flex justify-end mt-7 gap-4">
				{!!stories.length &&
					stories.map((story) => (
						<div
							key={story.title}
							onClick={closeMenu}
							className="w-full p-4 rounded-xl bg-no-repeat font-normal text-gray-200"
							style={{
								backgroundImage: `url(${story.background.responsiveImage.src})`,
								backgroundSize: 'contain',
							}}
						>
							<DatoLink fragment={story.link as DatoLinkFragment}>
								<p className="mb-1">{story.title}</p>
								<p className="text-[13px]">{story.text}</p>
							</DatoLink>
						</div>
					))}

				{!!extraButtons.length &&
					extraButtons.map((button, i) => (
						<div key={button.label} onClick={closeMenu}>
							<DatoLink fragment={button as DatoLinkFragment}>
								<Button
									color={i % 2 ? 'primary' : 'tertiary'}
									title={button.label}
								>
									{button.label}
								</Button>
							</DatoLink>
						</div>
					))}
			</div>
		</div>
	)
}

export function DropdownMenuContentBlock({
	title,
	image,
	description,
	titleLink,
	links,
	colsNumber = 1,
	showIcon,
	showLinkDescriptions,
	className,
	closeMenu,
}: {
	title: string
	image?: ResponsiveImage
	description: string
	titleLink: InternalLinkRecord
	links: InternalLinkRecord[]
	colsNumber?: number
	showIcon?: boolean
	showLinkDescriptions?: boolean
	className?: string
	closeMenu?: () => void
}) {
	return (
		<div className={cn('flex flex-col', className)}>
			{image?.src && (
				<div key={titleLink.label} onClick={closeMenu}>
					<DatoLink
						fragment={titleLink as DatoLinkFragment}
						className="group pb-1"
					>
						<div className="flex justify-between items-center mt-5 lg:mt-0 mb-4 rounded-lg overflow-hidden">
							{image && (
								<DatoImage
									data={image}
									layout="responsive"
									objectFit="cover"
									className="h-32 lg:h-52 xl:h-64 2xl:h-72 group-hover:scale-110 duration-150 ease-out"
								/>
							)}
						</div>
						<div className="flex items-center">
							{!!titleLink.icon && showIcon ? (
								<Image
									placeholder="empty"
									width={20}
									height={20}
									className="mr-3 lg:invert dark:lg:invert-0 w-auto h-auto"
									alt={titleLink.label || 'Menu icon'}
									src={titleLink.icon.url || '/images/image-placeholder.svg'}
								/>
							) : null}
							<span>{titleLink.label}</span>
						</div>
						{description && (
							<p className="hidden lg:block mt-3 text-xs dark:text-gray-300 text-gray-500">
								{description}
							</p>
						)}
					</DatoLink>
				</div>
			)}
			{!image?.src && !!links?.length && (
				<>
					<div
						onClick={closeMenu}
						className={description ? 'min-h-32' : 'min-h-14'}
					>
						<DatoLink
							key={titleLink.label}
							fragment={titleLink as DatoLinkFragment}
							className="group/link flex justify-between mb-2"
						>
							<div className="mb-2 mt-0 overflow-visible text-lg font-normal">
								{title}
							</div>
							<div>
								<Button
									variant="outline"
									color="adaptive"
									size="sm"
									className="!border-gray-600 group-hover/link:!border-gray-900 group-hover/link:dark:!border-white"
									title="arrow"
								/>
							</div>
						</DatoLink>
						{description && (
							<p className="pb-4 text-xs text-gray-500 dark:text-gray-300 stroke-gray-900 dark:stroke-white">
								{description}
							</p>
						)}
					</div>
					<div
						className={cn(
							'pt-6 pb-1 border-0 border-t border-gray-600 lg:grid grid-flow-row gap-5',
							colsNumber < 3 && links.length > 4
								? 'grid-cols-2'
								: 'auto-rows-max',
						)}
					>
						{links.map((link) => (
							<div key={link.label} onClick={closeMenu}>
								<DatoLink
									fragment={link as DatoLinkFragment}
									className="flex items-center pb-4 lg:pb-0 hover:contrast-0 dark:hover:brightness-50"
								>
									{showIcon && !!link.icon && (
										<div
											className={cn(
												'mr-3 w-6',
												link.color?.hex && 'lg:w-10 lg:mb-7',
											)}
											style={{
												borderBottom: link.color?.hex
													? `2px solid ${link.colorTheme.hex}`
													: '',
												paddingBottom: link.color?.hex ? `4px` : '',
											}}
										>
											<Image
												placeholder="empty"
												width={20}
												height={20}
												className="mx-auto lg:invert dark:lg:invert-0 w-auto"
												alt={link.icon.alt || link.label || 'Menu item icon'}
												src={link.icon.url || '/images/image-placeholder.svg'}
											/>
										</div>
									)}
									<div>
										<span>{link.label}</span>
										{showLinkDescriptions && (
											<p className="mt-1 text-xs">
												{link.link.seo?.description}
											</p>
										)}
									</div>
								</DatoLink>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	)
}
